/**
 * Authentication Selectors
 * 
 * SOLID Principles Applied:
 * - Single Responsibility: Only handles data access/queries from state
 * - Separation of Concerns: Isolated from state mutations and side effects
 * - Interface Segregation: Each selector serves a specific purpose
 * 
 * This file contains all selectors for accessing auth state:
 * - User data selectors
 * - Authentication status selectors
 * - Loading state selectors
 * - Error selectors
 * - Role-based selectors
 * 
 * Benefits:
 * - Centralized data access logic
 * - Easy to add memoization with reselect if needed
 * - Type-safe data extraction
 * - Reusable across components
 */

import { IAuthState, UserRole } from '../../../types/auth.types';

// ============================================
// STATE TYPE (for selector parameters)
// ============================================

type RootState = { auth: IAuthState };

// ============================================
// USER SELECTORS
// ============================================

/**
 * Select authenticated user object
 * 
 * @returns User object or null
 * 
 * Usage:
 *   const user = useAppSelector(selectUser);
 */
export const selectUser = (state: RootState) => state.auth.user;

/**
 * Select user's ID
 * 
 * @returns User ID string or undefined
 */
export const selectUserId = (state: RootState) => state.auth.user?.id;

/**
 * Select user's email address
 * 
 * @returns Email string or undefined
 */
export const selectUserEmail = (state: RootState) => state.auth.user?.email;

/**
 * Select user's full name
 * 
 * @returns Full name string or undefined
 */
export const selectUserFullName = (state: RootState) => state.auth.user?.fullName;

/**
 * Select user's role
 * 
 * @returns UserRole or undefined
 */
export const selectUserRole = (state: RootState) => state.auth.user?.role;

/**
 * Select if user's email is verified
 * 
 * @returns Boolean indicating email verification status
 */
export const selectIsEmailVerified = (state: RootState) => 
  state.auth.user?.isEmailVerified;

// ============================================
// AUTHENTICATION STATUS SELECTORS
// ============================================

/**
 * Select authentication status
 * 
 * @returns Boolean indicating if user is authenticated
 * 
 * Usage:
 *   const isAuthenticated = useAppSelector(selectIsAuthenticated);
 */
export const selectIsAuthenticated = (state: RootState) => 
  state.auth.isAuthenticated;

/**
 * Select access token
 * 
 * @returns Access token string or null
 */
export const selectAccessToken = (state: RootState) => 
  state.auth.accessToken;

/**
 * Select refresh token
 * 
 * @returns Refresh token string or null
 */
export const selectRefreshToken = (state: RootState) => 
  state.auth.refreshToken;

/**
 * Select last activity timestamp
 * 
 * @returns ISO timestamp string or null
 */
export const selectLastActivity = (state: RootState) => 
  state.auth.lastActivity;

// ============================================
// LOADING STATE SELECTORS
// ============================================

/**
 * Select global auth loading state
 * 
 * @returns Boolean indicating if any auth operation is in progress
 */
export const selectAuthLoading = (state: RootState) => 
  state.auth.isLoading;

/**
 * True after useAuthInit finishes (success or failure).
 * Route guards should use this instead of isLoading.
 */
export const selectAuthBootstrapComplete = (state: RootState) =>
  state.auth.bootstrapComplete;

/**
 * Message to show on login after session expiry.
 */
export const selectSessionMessage = (state: RootState) =>
  state.auth.sessionMessage;

/**
 * Select login loading state
 * 
 * @returns Boolean indicating if login is in progress
 */
export const selectIsLoggingIn = (state: RootState) => 
  state.auth.isLoggingIn;

/**
 * Select register loading state
 * 
 * @returns Boolean indicating if registration is in progress
 */
export const selectIsRegistering = (state: RootState) => 
  state.auth.isRegistering;

/**
 * Select logout loading state
 * 
 * @returns Boolean indicating if logout is in progress
 */
export const selectIsLoggingOut = (state: RootState) => 
  state.auth.isLoggingOut;

// ============================================
// ERROR SELECTORS
// ============================================

/**
 * Select auth error message
 * 
 * @returns Error message string or null
 */
export const selectAuthError = (state: RootState) => 
  state.auth.error;

/**
 * Select if there is an error
 * 
 * @returns Boolean indicating if error exists
 */
export const selectHasError = (state: RootState) => 
  state.auth.error !== null;

// ============================================
// COMPUTED/DERIVED SELECTORS
// ============================================

/**
 * Check if user has a specific role
 * 
 * @param state - Root state
 * @param role - Role to check
 * @returns Boolean indicating if user has the role
 * 
 * Usage:
 *   const isAdmin = useAppSelector((state) => selectHasRole(state, UserRole.ADMIN));
 */
export const selectHasRole = (state: RootState, role: string) => 
  state.auth.user?.role === role;

/**
 * Check if user is admin
 * 
 * @returns Boolean indicating if user is admin
 */
export const selectIsAdmin = (state: RootState) => 
  state.auth.user?.role === UserRole.ADMIN;

/**
 * Check if user is seller
 * 
 * @returns Boolean indicating if user is seller
 */
export const selectIsSeller = (state: RootState) => 
  state.auth.user?.role === UserRole.SELLER;

/**
 * Check if user is customer
 * 
 * @returns Boolean indicating if user is customer
 */
export const selectIsCustomer = (state: RootState) => 
  state.auth.user?.role === UserRole.REGULAR;

/**
 * Select complete auth session info
 * 
 * @returns Object with user, tokens, and auth status
 */
export const selectAuthSession = (state: RootState) => ({
  user: state.auth.user,
  isAuthenticated: state.auth.isAuthenticated,
  accessToken: state.auth.accessToken,
  lastActivity: state.auth.lastActivity,
});

/**
 * Select all loading states
 * 
 * @returns Object with all loading flags
 */
export const selectAllLoadingStates = (state: RootState) => ({
  isLoading: state.auth.isLoading,
  isLoggingIn: state.auth.isLoggingIn,
  isRegistering: state.auth.isRegistering,
  isLoggingOut: state.auth.isLoggingOut,
});

